import { useEffect, useReducer } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import i18n, { loadLanguage } from './config';

type Namespace =
  | 'common'
  | 'auth'
  | 'invoices'
  | 'quotes'
  | 'qrBill'
  | 'onboarding'
  | 'subscribe'
  | 'company'
  | 'templates'
  | 'clients'
  | 'products'
  | 'productLists'
  | 'account'
  | 'dashboard'
  | 'support'
  | 'employees'
  | 'payslips'
  | 'admin'
  | 'adminStats'
  | 'recurringInvoices'
  | 'feedback'
  | 'trialBanner'
  | 'subscriptionBanner'
  | 'absences'
  | 'supplierBills'
  | 'financialStatements'
  | 'ledger'
  | 'annualAccounts'
  | 'fixedAssets'
  | 'vat'
  | 'portal'
  | 'fiduciaire'
  | 'apiKeys'
  | 'sandbox'
  | 'zefix'
  | 'invoiceImport';

export function useTranslation(ns?: Namespace | Namespace[]) {
  return useI18nTranslation(ns);
}

export function useLanguage() {
  const { i18n: instance } = useI18nTranslation();
  return {
    language: instance.language,
    // Bundles are code-split per language, so fetch the target's strings
    // before switching — otherwise the UI renders raw keys for a frame.
    changeLanguage: async (lng: string) => {
      await loadLanguage(lng);
      return instance.changeLanguage(lng);
    },
  };
}

/**
 * Ensures `lng`'s bundles are in the store, for the document-language previews
 * that call `i18n.getFixedT(lng, …)`: an invoice can be in German while the UI
 * runs in French, and only the UI language is bundled at startup. Re-renders
 * once the strings land — until then `getFixedT` falls back to French.
 */
export function useDocumentLanguage(lng: string) {
  const [, forceRender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    let cancelled = false;
    loadLanguage(lng).then(() => {
      if (!cancelled) forceRender();
    });
    return () => {
      cancelled = true;
    };
  }, [lng]);
}

export { i18n };
